import { combineReducers } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';
import { normalizeFacetGroup } from './lib/normalizeFacetGroup';
import {
  templatesQueryReducer,
  selectTemplatesActiveTab,
  selectTemplatesFilters,
  selectTemplatesTagsMode,
  selectTemplatesHasLoadedOnce,
  selectTemplatesOrdering,
  selectTemplatesSearch,
  templatesChannelTabChanged,
  templatesFiltersChanged,
  templatesTagsModeChanged,
  templatesOrderingChanged,
  templatesSearchChanged,
} from './templatesQuerySlice';
import {
  selectTemplatesFacets,
  selectTemplatesFacetsStatus,
  selectTemplatesFacetsTabs,
  templatesFacetsReducer,
} from './templatesFacetsSlice';
import {
  selectTemplatesItems,
  selectTemplatesListStatus,
  selectTemplatesListTabs,
  selectTemplatesPage,
  selectTemplatesPageSize,
  selectTemplatesTotalPages,
  templatesListReducer,
  templatesTabPageChanged,
  templatesTabPageSizeChanged,
} from './templatesListSlice';
import {
  createMessageTemplate,
  createMessageTemplateForTenants,
  loadMessageTemplateById,
  loadMessageTemplateFacets,
  loadMessageTemplates,
  loadTemplatesPage,
  setMessageTemplateStatus,
  updateMessageTemplate,
} from './templatesThunks';
import {
  selectTemplatesCampaigns,
  selectTemplatesCampaignsStatus,
  selectTemplatesTags,
  selectTemplatesTagsStatus,
  selectTemplatesTriggers,
  selectTemplatesTriggersStatus,
  selectTemplatesVariables,
  selectTemplatesVariablesStatus,
  selectTemplatesVariableSearch,
  selectTemplatesTenantSettingsStatus,
  selectTemplatesTenantLogo,
  selectTemplatesTenantSender,
  selectTemplatesTenantVariablesSyncedAt,
  selectTemplatesTenantAdditionalLanguages,
  selectTemplatesDisclosuresStatus,
  selectTemplatesDisclosures,
  selectTemplatesTenantsStatus,
  selectTemplatesTenants,
  templatesMetaReducer,
} from './templatesMetaSlice';
import {
  loadDisclosures,
  loadTemplateCampaigns,
  loadTemplateTags,
  loadTemplateTenants,
  loadTemplateTriggers,
  loadTemplateVariables,
  loadTenantSettings,
  searchTemplateVariables,
  templatesVariablesReset,
  templatesVariableSearchReset,
} from './templatesMetaThunks';
import {
  templatesTriggerValidationReducer,
  validateTriggerAssignments,
  triggerValidationCleared,
  selectTriggerValidationRowErrors,
} from './templatesTriggerValidationSlice';

const templatesReducer = combineReducers({
  query: templatesQueryReducer,
  list: templatesListReducer,
  facets: templatesFacetsReducer,
  meta: templatesMetaReducer,
  triggerValidation: templatesTriggerValidationReducer,
});

export default templatesReducer;

export {
  loadMessageTemplates,
  loadMessageTemplateFacets,
  loadTemplatesPage,
  createMessageTemplate,
  createMessageTemplateForTenants,
  loadMessageTemplateById,
  updateMessageTemplate,
  setMessageTemplateStatus,
  loadDisclosures,
  loadTemplateCampaigns,
  loadTemplateTags,
  loadTemplateTenants,
  loadTemplateTriggers,
  loadTemplateVariables,
  loadTenantSettings,
  searchTemplateVariables,
  templatesVariablesReset,
  templatesVariableSearchReset,
  selectTemplatesActiveTab,
  selectTemplatesSearch,
  selectTemplatesOrdering,
  selectTemplatesFilters,
  selectTemplatesTagsMode,
  selectTemplatesHasLoadedOnce,
  selectTemplatesListStatus,
  selectTemplatesItems,
  selectTemplatesPage,
  selectTemplatesPageSize,
  selectTemplatesTotalPages,
  selectTemplatesFacetsStatus,
  selectTemplatesFacets,
  selectTemplatesCampaignsStatus,
  selectTemplatesCampaigns,
  selectTemplatesTagsStatus,
  selectTemplatesTags,
  selectTemplatesTriggersStatus,
  selectTemplatesTriggers,
  selectTemplatesVariablesStatus,
  selectTemplatesVariables,
  selectTemplatesVariableSearch,
  selectTemplatesTenantSettingsStatus,
  selectTemplatesTenantLogo,
  selectTemplatesTenantSender,
  selectTemplatesTenantVariablesSyncedAt,
  selectTemplatesTenantAdditionalLanguages,
  selectTemplatesDisclosuresStatus,
  selectTemplatesDisclosures,
  selectTemplatesTenantsStatus,
  selectTemplatesTenants,
  templatesChannelTabChanged,
  templatesSearchChanged,
  templatesOrderingChanged,
  templatesFiltersChanged,
  templatesTagsModeChanged,
  validateTriggerAssignments,
  triggerValidationCleared,
  selectTriggerValidationRowErrors,
};

export const templatesPageChanged = (nextPage) => (dispatch, getState) => {
  const state = getState();
  const tabKey = selectTemplatesActiveTab(state);
  dispatch(
    templatesTabPageChanged({
      tabKey,
      page: nextPage,
    }),
  );
};

export const templatesPageSizeChanged = (nextPageSize) => (dispatch, getState) => {
  const state = getState();
  const tabKey = selectTemplatesActiveTab(state);
  dispatch(
    templatesTabPageSizeChanged({
      tabKey,
      pageSize: nextPageSize,
    }),
  );
};

export const selectTemplatesTabs = (state) => {
  const listTabs = selectTemplatesListTabs(state);
  const facetsTabs = selectTemplatesFacetsTabs(state);

  return {
    email: {
      ...(listTabs?.email ?? {}),
      facetsStatus: facetsTabs?.email?.facetsStatus ?? LOADING_STATUS.IDLE,
    },
    text: {
      ...(listTabs?.text ?? {}),
      facetsStatus: facetsTabs?.text?.facetsStatus ?? LOADING_STATUS.IDLE,
    },
  };
};

export const selectTemplatesFacetTags = (state) =>
  normalizeFacetGroup(selectTemplatesFacets(state)?.tags);

export const selectTemplatesFacetTagsExclude = (state) =>
  normalizeFacetGroup(selectTemplatesFacets(state)?.tags_exclude);

export const selectTemplatesFacetStatus = (state) =>
  normalizeFacetGroup(selectTemplatesFacets(state)?.status);

export const selectTemplatesFacetLanguage = (state) =>
  normalizeFacetGroup(selectTemplatesFacets(state)?.language);

export const selectTemplatesFacetType = (state) =>
  normalizeFacetGroup(selectTemplatesFacets(state)?.type);

export const selectTemplatesFacetChannels = (state) =>
  normalizeFacetGroup(
    selectTemplatesFacets(state)?.channel ?? selectTemplatesFacets(state)?.channels,
  );

export const selectTemplatesFacetCampaigns = (state) =>
  normalizeFacetGroup(selectTemplatesFacets(state)?.campaigns);
